import { Fragment, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search, Filter, Trash2, Download } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useLookups, useCustomFields } from "@/lib/lookups";
import { PageLoader, EmptyState, Modal, useToast, useConfirm, Progress } from "@/components/ui";
import { HealthBadge, StatusBadge, PriorityDot } from "@/components/badges";
import { Field, TextInput, TextArea, Select, UserPicker, EditableCell } from "@/components/fields";
import { money, pct, fmtDate } from "@/lib/format";
import type { Activity } from "@shared/types";
import type { ProjectBundle } from "../ProjectDetail";
import { useActivities, useActivityMutations } from "./useActivities";
import { ActivityDrawer } from "./ActivityDrawer";

export function Schedule({ bundle }: { bundle: ProjectBundle }) {
  const projectId = bundle.project.id;
  const { data: activities, isLoading } = useActivities(projectId);
  const { byCategory } = useLookups();
  const customDefs = useCustomFields("activity").filter((d) => d.showInGrid);
  const m = useActivityMutations(projectId);
  const toast = useToast();
  const { confirm, node: confirmNode } = useConfirm();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [phaseFilter, setPhaseFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [healthFilter, setHealthFilter] = useState("");
  const [mineOnly, setMineOnly] = useState(false);
  const [adding, setAdding] = useState(false);
  const { user } = useAuth();
  const canManage = bundle.access.canManageActivities;

  const selectedId = params.get("activity");
  const selected = activities?.find((a) => a.id === selectedId) || null;

  const filtered = useMemo(() => {
    let list = activities ?? [];
    if (q) list = list.filter((a) => (a.title + " " + (a.description ?? "") + " " + (a.responsibleUserName ?? "")).toLowerCase().includes(q.toLowerCase()));
    if (phaseFilter) list = list.filter((a) => a.phaseId === phaseFilter);
    if (statusFilter) list = list.filter((a) => a.status === statusFilter);
    if (healthFilter) list = list.filter((a) => a.scheduleHealth === healthFilter);
    if (mineOnly) list = list.filter((a) => a.responsibleUserId === user?.id);
    return list;
  }, [activities, q, phaseFilter, statusFilter, healthFilter, mineOnly, user]);

  const grouped = useMemo(() => {
    const map = new Map<string, { phaseId: string | null; name: string; items: Activity[] }>();
    for (const p of bundle.phases) map.set(p.id, { phaseId: p.id, name: p.name, items: [] });
    map.set("none", { phaseId: null, name: "Unassigned", items: [] });
    for (const a of filtered) (map.get(a.phaseId ?? "none") ?? map.get("none")!).items.push(a);
    return [...map.values()].filter((g) => g.items.length > 0);
  }, [filtered, bundle.phases]);

  const totals = useMemo(() => {
    const list = filtered;
    const wSum = list.reduce((s, a) => s + (a.weightage || 0), 0);
    const wProg = list.reduce((s, a) => s + a.weightedProgress, 0);
    return {
      count: list.length,
      planned: list.reduce((s, a) => s + a.plannedCost, 0),
      actual: list.reduce((s, a) => s + a.actualCost, 0),
      overall: wSum > 0 ? (wProg / wSum) * 100 : 0,
    };
  }, [filtered]);

  if (isLoading) return <PageLoader />;

  const exportCsv = () => {
    const cols = ["Phase", "Activity", "Responsible", "Priority", "Start", "End", "Weightage", "Planned", "Actual", "% Complete", "Status", "Schedule Health"];
    const rows = filtered.map((a) => [
      a.phaseName ?? "", a.title, a.responsibleUserName ?? a.responsibleRole ?? "", a.priority,
      a.startDate ?? "", a.endDate ?? "", a.weightage, a.plannedCost, a.actualCost, a.percentComplete, a.status, a.scheduleHealth,
    ]);
    const csv = [cols, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = `${bundle.project.code || "schedule"}.csv`; link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={14} className="pointer-events-none absolute left-2.5 top-2.5 text-ink-faint" />
          <input className="input !w-56 !pl-8" placeholder="Search activities…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Select className="!w-auto" value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value)}>
          <option value="">All phases</option>
          {bundle.phases.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Select className="!w-auto" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          {byCategory("status").map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </Select>
        <Select className="!w-auto" value={healthFilter} onChange={(e) => setHealthFilter(e.target.value)}>
          <option value="">All health</option>
          {["on_track", "ahead", "delayed", "planned", "done", "cancelled"].map((h) => <option key={h} value={h}>{h}</option>)}
        </Select>
        <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} /> My tasks</label>
        <div className="flex-1" />
        <button className="btn-ghost text-xs" onClick={exportCsv}><Download size={14} /> CSV</button>
        {canManage && <button className="btn-primary" onClick={() => setAdding(true)}><Plus size={16} /> Add activity</button>}
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Filter} title="No activities match" hint={canManage ? "Add the first activity to start building the schedule." : "Nothing to show with these filters."}
          action={canManage && <button className="btn-primary" onClick={() => setAdding(true)}><Plus size={16} /> Add activity</button>} />
      ) : (
        <div className="grid-scroll overflow-x-auto rounded-xl border border-line">
          <table className="w-full min-w-[1100px] border-collapse bg-surface text-sm">
            <thead className="sticky top-0 z-10">
              <tr>
                <th className="th w-8">#</th>
                <th className="th min-w-[240px]">Activity</th>
                <th className="th">Responsible</th>
                <th className="th w-24">Priority</th>
                <th className="th w-28">Start</th>
                <th className="th w-28">End</th>
                <th className="th w-20 text-right">Weight</th>
                <th className="th w-28 text-right">Planned</th>
                <th className="th w-28 text-right">Actual</th>
                <th className="th w-32">% Complete</th>
                <th className="th w-32">Status</th>
                <th className="th w-28">Health</th>
                {customDefs.map((d) => <th key={d.id} className="th">{d.label}</th>)}
                {canManage && <th className="th w-10" />}
              </tr>
            </thead>
            <tbody>
              {grouped.map((g) => (
                <Fragment key={g.phaseId ?? "none"}>
                  <tr className="bg-surface-2">
                    <td colSpan={12 + customDefs.length + (canManage ? 1 : 0)} className="px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-ink-soft">
                      {g.name} <span className="ml-2 font-normal text-ink-faint">({g.items.length})</span>
                    </td>
                  </tr>
                  {g.items.map((a, i) => (
                    <tr key={a.id} className="border-b border-line hover:bg-surface-2/40">
                      <td className="td text-ink-faint">{i + 1}</td>
                      <td className="td">
                        <button className="text-left font-medium hover:text-brand-600" onClick={() => setParams({ activity: a.id })}>
                          {a.title}
                        </button>
                        {a.attachmentCount > 0 && <span className="ml-1.5 text-[11px] text-ink-faint">📎{a.attachmentCount}</span>}
                        {a.description && <p className="mt-0.5 line-clamp-1 text-xs text-ink-faint">{a.description}</p>}
                      </td>
                      <td className="td">
                        {canManage ? (
                          <div className="w-40"><UserPicker value={a.responsibleUserId} onChange={(v) => m.update.mutate({ id: a.id, responsibleUserId: v })} /></div>
                        ) : (a.responsibleUserName || a.responsibleRole || "—")}
                      </td>
                      <td className="td">
                        {canManage
                          ? <EditableCell type="select" value={a.priority} options={byCategory("priority").map((p) => ({ value: p.value, label: p.label }))} onSave={(v) => m.update.mutate({ id: a.id, priority: v })} />
                          : <PriorityDot value={a.priority} />}
                      </td>
                      <td className="td">{canManage ? <EditableCell type="date" value={a.startDate} onSave={(v) => m.update.mutate({ id: a.id, startDate: v })} /> : fmtDate(a.startDate)}</td>
                      <td className="td">{canManage ? <EditableCell type="date" value={a.endDate} onSave={(v) => m.update.mutate({ id: a.id, endDate: v })} /> : fmtDate(a.endDate)}</td>
                      <td className="td text-right">{canManage ? <EditableCell type="number" align="right" value={a.weightage} onSave={(v) => m.update.mutate({ id: a.id, weightage: Number(v) })} /> : a.weightage}</td>
                      <td className="td text-right tabular-nums">{canManage ? <EditableCell type="number" align="right" value={a.plannedCost} onSave={(v) => m.update.mutate({ id: a.id, plannedCost: Number(v) })} /> : money(a.plannedCost, bundle.project.currency)}</td>
                      <td className="td text-right tabular-nums">{(canManage || a.responsibleUserId === user?.id) ? <EditableCell type="number" align="right" value={a.actualCost} onSave={(v) => m.update.mutate({ id: a.id, actualCost: Number(v) })} /> : money(a.actualCost, bundle.project.currency)}</td>
                      <td className="td">
                        <div className="flex items-center gap-1.5">
                          <Progress value={a.percentComplete} className="w-16" />
                          <span className="text-xs tabular-nums">{pct(a.percentComplete)}</span>
                        </div>
                      </td>
                      <td className="td">
                        {(canManage || a.responsibleUserId === user?.id)
                          ? <EditableCell type="select" value={a.status} options={byCategory("status").map((s) => ({ value: s.value, label: s.label }))}
                              onSave={(v) => (canManage ? m.update.mutate({ id: a.id, status: v }) : m.status.mutate({ id: a.id, status: v }))} />
                          : <StatusBadge value={a.status} />}
                      </td>
                      <td className="td"><HealthBadge health={a.scheduleHealth} /></td>
                      {customDefs.map((d) => (
                        <td key={d.id} className="td text-xs text-ink-soft">
                          {formatCustom(a.custom?.[d.key], d.type)}
                        </td>
                      ))}
                      {canManage && (
                        <td className="td">
                          <button className="btn-ghost !p-1 text-ink-faint hover:text-st-delayed"
                            onClick={async () => { if (await confirm("Delete activity", `Delete "${a.title}"? This cannot be undone.`, true)) { m.remove.mutate(a.id); toast.push("success", "Activity deleted"); } }}>
                            <Trash2 size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-surface-2 font-semibold">
                <td className="td" />
                <td className="td">Totals ({totals.count})</td>
                <td className="td" colSpan={5} />
                <td className="td text-right tabular-nums">{money(totals.planned, bundle.project.currency)}</td>
                <td className="td text-right tabular-nums">{money(totals.actual, bundle.project.currency)}</td>
                <td className="td">{pct(totals.overall)} weighted</td>
                <td className="td" colSpan={2 + customDefs.length + (canManage ? 1 : 0)} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {selected && <ActivityDrawer activity={selected} bundle={bundle} onClose={() => setParams({})} />}
      {adding && <AddActivityModal bundle={bundle} onClose={() => setAdding(false)} />}
      {confirmNode}
    </>
  );
}

function formatCustom(v: unknown, type: string): string {
  if (v == null || v === "") return "—";
  if (type === "checkbox") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}

function AddActivityModal({ bundle, onClose }: { bundle: ProjectBundle; onClose: () => void }) {
  const m = useActivityMutations(bundle.project.id);
  const toast = useToast();
  const { byCategory } = useLookups();
  const [f, setF] = useState({
    title: "", description: "", phaseId: bundle.phases[0]?.id || "", responsibleUserId: "", responsibleRole: "",
    priority: "Medium", startDate: "", endDate: "", weightage: "", plannedCost: "", status: "Not Started", notes: "",
  });

  return (
    <Modal open onClose={onClose} title="Add activity" wide
      footer={<>
        <button className="btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!f.title || m.create.isPending}
          onClick={() => m.create.mutate({ ...f, weightage: Number(f.weightage) || 0, plannedCost: Number(f.plannedCost) || 0 },
            { onSuccess: () => { toast.push("success", "Activity added"); onClose(); } })}>
          Add activity
        </button>
      </>}>
      <div className="grid gap-3.5 sm:grid-cols-2">
        <div className="sm:col-span-2"><Field label="Activity title" required><TextInput autoFocus value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field></div>
        <div className="sm:col-span-2"><Field label="Description / sub-activity"><TextArea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field></div>
        <Field label="Phase"><Select value={f.phaseId} onChange={(e) => setF({ ...f, phaseId: e.target.value })}>
          <option value="">Unassigned</option>
          {bundle.phases.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select></Field>
        <Field label="Priority"><Select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })}>
          {byCategory("priority").map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </Select></Field>
        <Field label="Responsible person"><UserPicker value={f.responsibleUserId || null} onChange={(v) => setF({ ...f, responsibleUserId: v || "" })} /></Field>
        <Field label="Responsible role"><Select value={f.responsibleRole} onChange={(e) => setF({ ...f, responsibleRole: e.target.value })}>
          <option value="">—</option>
          {byCategory("responsible_role").map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </Select></Field>
        <Field label="Start date"><TextInput type="date" value={f.startDate} onChange={(e) => setF({ ...f, startDate: e.target.value })} /></Field>
        <Field label="End date"><TextInput type="date" value={f.endDate} onChange={(e) => setF({ ...f, endDate: e.target.value })} /></Field>
        <Field label="Weightage" hint="Share of the project's total effort"><TextInput type="number" step="0.01" value={f.weightage} onChange={(e) => setF({ ...f, weightage: e.target.value })} /></Field>
        <Field label="Planned cost"><TextInput type="number" value={f.plannedCost} onChange={(e) => setF({ ...f, plannedCost: e.target.value })} /></Field>
        <Field label="Initial status"><Select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
          {byCategory("status").map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </Select></Field>
      </div>
    </Modal>
  );
}
